import React from 'react';
import { Award, Star, Zap, BarChart3, ChevronRight, Search, Shield, BarChart2, Sparkles, Server, Code, AlertTriangle, TrendingUp, Target, ArrowUp, ArrowDown, Minus, Check, Brain, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

// Composants existants
import GoogleReviewsBox from '../components/GoogleReviewsBox';
import PageSpeedBox from '../components/PageSpeedBox';
import ExitPopupDemo from '../components/ExitPopupDemo';
import QuizCompletionCta from '../components/QuizCompletionCta';

// Services et hooks
import { useTestPageData } from '../features/testPage/hooks/useTestPageData';

// Types
interface Prospect {
  id?: string;
  prenom?: string;
  nom?: string;
  site_url?: string;
  score?: number;
  quiz_fait?: boolean;
  quiz_commence?: boolean;
  token?: string;
  metadata?: {
    company?: string;
    domain_emelia?: string;
    industry?: string;
    location?: string;
    companyDescription?: string;
  };
}

// Utilisation de l'interface existante de pagespeedData

// Utilitaires
const extractDomain = (url: string): string => {
  if (!url) return '';
  try {
    if (url.startsWith('http')) {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    }
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('?')[0];
  } catch (error) {
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('?')[0];
  }
};

const getDomainFromProspect = (prospect: Prospect | null): string => {
  if (!prospect) return '';
  return prospect.site_url || prospect.metadata?.domain_emelia || '';
};

const formatPageSpeedScore = (score: number | null | undefined, fallback: number = 0): number => {
  if (score === null || score === undefined) return fallback;
  if (score <= 1) return Math.round(score * 100);
  return Math.round(score);
};

const getAnimationDelay = (index: number, baseDelay: number = 100): string => {
  return `${baseDelay + index * 100}ms`;
};

const splitIntoParagraphs = (text: string): string[] => {
  if (!text) return [];
  return text.split(/\n\s*\n/).filter(p => p.trim().length > 0).map(p => p.trim());
};

/**
 * Page de test refactorisée avec une architecture modulaire
 */
export const TestPageRefactored: React.FC = () => {
  const {
    // États
    loading,
    isLoadingAi,
    isLoadingIntro,
    prospect,
    cmsData,
    pagespeedData,
    // keywordsData, // Non utilisé
    aiRecommendations,
    personalizedIntro,
    error,
    aiError,
    keyComponentKey,
    mockData,
    
    // Actions
    refreshAIRecommendations,
    refreshGoogleReviews,
    // updateState // Non utilisé
  } = useTestPageData();



  // Rendu conditionnel pour les erreurs
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Erreur</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-center">
            <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
              <div className="flex-shrink-0">
                <img src="/images/smart_impact_logo.jpg" alt="Smart Impact" className="h-12 object-contain" />
              </div>
              <div className="text-center">
                <p className="text-gray-600 text-xs sm:text-sm font-medium">
                  Agence digitale • Référencement • IA • Développement
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section - Full Width */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20 lg:py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-orange-400/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-gradient-to-r from-orange-500/5 to-transparent rounded-full blur-3xl"></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.02%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto text-center">
            
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-8 leading-tight animate-fadeSlideUp text-white" style={{animationDelay: '100ms'}}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                Découvrez ce qui freine le trafic de {prospect?.metadata?.company || prospect?.nom || extractDomain(getDomainFromProspect(prospect)) || 'votre entreprise'} ?
              </span>
            </h1>
                
            {/* Introduction personnalisée */}
            {isLoadingIntro ? (
              <div className="mx-auto mb-12 max-w-4xl text-center animate-pulse">
                <div className="h-6 bg-gray-600 rounded w-3/4 mx-auto mb-3"></div>
                <div className="h-6 bg-gray-600 rounded w-full mx-auto mb-3"></div>
                <div className="h-6 bg-gray-600 rounded w-5/6 mx-auto mb-3"></div>
                <div className="h-6 bg-gray-600 rounded w-2/3 mx-auto"></div>
                <p className="text-sm text-gray-400 mt-4">Génération de votre analyse personnalisée...</p>
              </div>
            ) : personalizedIntro ? (
              <div className="mx-auto mb-12 max-w-4xl text-center">
                {splitIntoParagraphs(personalizedIntro).map((paragraph: string, index: number) => (
                  <p key={index} className="mb-6 text-xl text-gray-300 leading-relaxed animate-fadeSlideUp" 
                     style={{animationDelay: getAnimationDelay(index, 200)}}>
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : (
              <p className="mx-auto mb-12 max-w-4xl text-xl text-gray-300 leading-relaxed animate-fadeSlideUp text-center" 
                 style={{animationDelay: '200ms'}} data-component-name="TestPageRefactored">
                Notre analyse est en préparation. Consultez les résultats détaillés ci-dessous en attendant.
              </p>
            )}
            
            {/* Site URL avec style moderne */}
            <div className="mb-12 animate-fadeSlideUp" style={{animationDelay: '300ms'}}>
              <div className="inline-block bg-white/10 rounded-2xl px-8 py-4 backdrop-blur-sm border border-white/20 shadow-2xl">
                <span className="font-mono text-2xl md:text-3xl font-bold text-orange-400">
                  {loading ? 'Chargement...' : 
                    error ? 'site-web.com' : 
                    getDomainFromProspect(prospect)
                  }
                </span>
              </div>
            </div>
            
            {/* CTA Principal Orange */}
            <div className="flex flex-col items-center gap-6 animate-fadeSlideUp" style={{animationDelay: '400ms'}}>
              <button className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-xl px-12 py-6 rounded-2xl shadow-2xl hover:shadow-orange-500/25 transform hover:scale-105 transition-all duration-300 inline-flex items-center">
                <Award className="mr-4" size={28} />
                Aller plus loin
              </button>
              
              {/* Badge de performance */}
              <div className="inline-flex items-center rounded-full bg-white/10 border border-white/20 px-6 py-3 text-lg font-medium text-gray-300 shadow-xl">
                <Star className="mr-3" size={20} />
                Score Global {prospect?.score || mockData.digitalScore}%
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Problèmes - Fond gris clair */}
      <section className="bg-gray-50 pt-[70px] pb-[140px]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Titre de section */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                <Award className="w-4 h-4 mr-2" />
                {loading ? 'Chargement...' : 
                  aiError ? 'Check-up digital gratuit' : 
                  `Check-up digital gratuit de ${prospect?.site_url ? extractDomain(prospect.site_url) : 'votre entreprise'}`
                }
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Analyse complète de votre présence digitale
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Découvrez les points d'amélioration qui peuvent transformer votre performance en ligne
              </p>
            </div>

            {/* Grille d'analyse - Cartes blanches avec bordures */}
            <div className="grid md:grid-cols-2 gap-8">
              
              {/* PageSpeed Insights - Card blanche */}
              <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="mb-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 mr-4">
                      <Zap size={24} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">PageSpeed Insights</h3>
                  </div>
                  <p className="text-gray-600 text-lg">
                    Vitesse de chargement de votre site web
                  </p>
                </div>
                
                <div className="min-h-[400px]">
                  <PageSpeedBox 
                    mobileScore={formatPageSpeedScore(pagespeedData?.mobile_performance_score, mockData.pageSpeed.mobile)} 
                    desktopScore={formatPageSpeedScore(pagespeedData?.desktop_performance_score, mockData.pageSpeed.desktop)}
                    isLoading={false}
                    prospectSector={prospect?.metadata?.industry || "d'activités"}
                    pagespeedData={pagespeedData ? {
                      performance_score: pagespeedData.mobile_performance_score || 0,
                      accessibility_score: pagespeedData.accessibility_score || 0,
                      best_practices_score: pagespeedData.best_practices_score || 0,
                      seo_score: pagespeedData.seo_score || 0,
                      mobile_performance_score: pagespeedData.mobile_performance_score,
                      desktop_performance_score: pagespeedData.desktop_performance_score,
                      mobile_data: pagespeedData.mobile_data,
                      desktop_data: pagespeedData.desktop_data
                    } : null}
                  />
                </div>
              </div>
              
              {/* Avis Google Business - Card blanche */}
              <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="mb-6">
                  <div className="flex items-center mb-4">
                    <div 
                      className="p-3 rounded-2xl bg-orange-100 text-orange-600 cursor-pointer hover:scale-110 transition-transform mr-4"
                      onClick={() => {
                        refreshGoogleReviews();
                        setTimeout(() => window.location.reload(), 500);
                      }}
                      title="Actualiser les avis Google"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" fill="currentColor"/>
                        <path d="M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6Z" fill="white"/>
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Avis Google Business</h3>
                  </div>
                  <p className="text-gray-600 text-lg">
                    Réputation en ligne et avis clients
                  </p>
                </div>
                
                <div className="min-h-[400px]">
                  <GoogleReviewsBox 
                    key={`google-reviews-${keyComponentKey}`}
                    businessName={prospect?.metadata?.company || prospect?.nom || 'Entreprise'}
                    location={prospect?.metadata?.location}
                    prospectId={prospect?.id || ''}
                    domain={getDomainFromProspect(prospect)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
          
      {/* Section Solutions - Fond blanc */}
      <section className="bg-white pt-[70px] pb-[140px]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Digital Score CTA - Tech Dashboard Style */}
            <div className="animate-fadeSlideUp" style={{animationDelay: '350ms'}}>
              <div className="max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  
                  {/* Left side - Content */}
                  <div>
                    {/* Status badge - Style TestPage */}
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                      <Award className="w-4 h-4 mr-2" />
                      Analyse digitale gratuite
                    </div>
                    
                    {/* Title - Style TestPage */}
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                      Envie d'aller plus loin avec 
                      <span className="text-orange-600"> {prospect?.metadata?.company || prospect?.nom || extractDomain(getDomainFromProspect(prospect)) || 'votre entreprise'}</span> ?
                    </h2>
                    
                    {/* Subtitle */}
                    <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                      Affinez votre feuille de route digitale avec un diagnostic complet et des recommandations personnalisées.
                    </p>
                    
                    {/* Features list - Style TestPage */}
                    <div className="space-y-4 mb-8">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                          <Search className="w-4 h-4" />
                        </div>
                        <span className="text-gray-700 font-medium">7 questions rapides</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                          <Shield className="w-4 h-4" />
                        </div>
                        <span className="text-gray-700 font-medium">Analyse sécurisée</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                          <BarChart2 className="w-4 h-4" />
                        </div>
                        <span className="text-gray-700 font-medium">Résultats instantanés</span>
                      </div>
                    </div>
                    
                    
                    {/* Trust indicators - Style TestPage */}
                    <div className="flex justify-center lg:justify-start items-center space-x-6 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Sans engagement
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        Données sécurisées
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                        Résultats immédiats
                      </span>
                    </div>
                  </div>
                  
                  {/* Right side - Citation inspirante */}
                  <div>
                    <div className="bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-700">
                      {/* Citation */}
                      <div className="text-center">
                        <div className="mb-6">
                          <div className="text-6xl text-orange-400 mb-4">"</div>
                          <blockquote className="text-xl font-medium text-white leading-relaxed mb-6">
                            Dans le monde numérique d'aujourd'hui, votre site web n'est pas juste une vitrine, 
                            c'est votre <span className="text-orange-400 font-semibold">principal vendeur</span> qui travaille 24h/24.
                          </blockquote>
                          <div className="flex items-center justify-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center">
                              <span className="text-white font-bold text-lg">JB</span>
                            </div>
                            <div className="text-left">
                              <div className="font-semibold text-white">Jeff Bezos</div>
                              <div className="text-sm text-gray-400">Fondateur d'Amazon</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Call to Action simple */}
                        <div className="pt-6 border-t border-gray-700">
                          <p className="text-gray-300 text-sm mb-4">
                            Découvrez comment optimiser votre présence digitale
                          </p>
                          <motion.button 
                            className="relative group w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl overflow-hidden"
                            whileHover={{ 
                              scale: 1.05,
                              boxShadow: "0 25px 50px -12px rgba(251, 146, 60, 0.4)"
                            }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {/* Effet de brillance animé */}
                            <motion.div 
                              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                              initial={{ x: "-100%" }}
                              animate={{ x: "100%" }}
                              transition={{ 
                                duration: 2, 
                                repeat: Infinity,
                                repeatDelay: 3,
                                ease: "easeInOut"
                              }}
                            />
                            
                            {/* Particules flottantes */}
                            <div className="absolute inset-0 pointer-events-none">
                              <motion.div 
                                className="absolute top-2 left-4 w-1 h-1 bg-white/40 rounded-full"
                                animate={{ 
                                  y: [0, -8, 0],
                                  opacity: [0.4, 0.8, 0.4]
                                }}
                                transition={{ 
                                  duration: 2, 
                                  repeat: Infinity,
                                  repeatType: "reverse" 
                                }}
                              />
                              <motion.div 
                                className="absolute top-3 right-6 w-1.5 h-1.5 bg-white/30 rounded-full"
                                animate={{ 
                                  y: [0, -6, 0],
                                  opacity: [0.3, 0.7, 0.3]
                                }}
                                transition={{ 
                                  duration: 2.5, 
                                  repeat: Infinity,
                                  repeatType: "reverse",
                                  delay: 0.5
                                }}
                              />
                            </div>
                            
                            <span className="relative flex items-center justify-center gap-3 text-lg">
                              <motion.div
                                animate={{ rotate: [0, 360] }}
                                transition={{ 
                                  duration: 3, 
                                  repeat: Infinity,
                                  ease: "linear"
                                }}
                              >
                                <Sparkles className="w-5 h-5" />
                              </motion.div>
                              <span className="font-extrabold tracking-wide">
                                Obtenir mon score gratuitement
                              </span>
                              <motion.div
                                className="group-hover:translate-x-1 transition-transform duration-300"
                                animate={{ x: [0, 3, 0] }}
                                transition={{ 
                                  duration: 2, 
                                  repeat: Infinity,
                                  repeatType: "reverse"
                                }}
                              >
                                <ChevronRight className="w-5 h-5" />
                              </motion.div>
                            </span>
                            
                            {/* Pulsation de fond */}
                            <motion.div 
                              className="absolute inset-0 bg-orange-400/20 rounded-xl"
                              animate={{ 
                                scale: [1, 1.05, 1],
                                opacity: [0, 0.3, 0]
                              }}
                              transition={{ 
                                duration: 2, 
                                repeat: Infinity,
                                repeatType: "reverse" 
                              }}
                            />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Analyse Technique - Fond gris */}
      <section className="bg-gray-50 pt-[70px] pb-[140px]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* CMS Info - Tech Dashboard Style */}
            <div className="animate-fadeSlideUp" style={{animationDelay: '400ms'}}>
              <div className="max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                      <Server className="w-4 h-4 mr-2" />
                      Analyse technique
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                      Technologie de <span className="text-orange-600">votre site</span>
                    </h3>
                    <p className="text-lg text-gray-600 mb-6">
                      Analyse du CMS et des technologies utilisées pour optimiser les performances et la sécurité
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                          <Code className="w-4 h-4" />
                        </div>
                        <span className="text-gray-700 font-medium">CMS détecté avec {cmsData?.confidence || 90}% de confiance</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                          <Shield className="w-4 h-4" />
                        </div>
                        <span className="text-gray-700 font-medium">Recommandations de sécurité incluses</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                          <Zap className="w-4 h-4" />
                        </div>
                        <span className="text-gray-700 font-medium">Optimisations techniques suggérées</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-white rounded-lg p-2 border border-gray-600">
                            {cmsData?.logo_url ? (
                              <img 
                                src={cmsData.logo_url} 
                                alt={`Logo ${cmsData.name}`} 
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <Server className="w-full h-full text-gray-600" />
                            )}
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-white">{cmsData?.name || 'CMS détecté'}</h4>
                            <p className="text-sm text-gray-300">Version {cmsData?.version || 'inconnue'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-orange-400">{cmsData?.confidence || 90}%</div>
                          <div className="text-xs text-gray-400">Confiance</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                          <div className="text-orange-400 text-sm font-medium mb-1">Sécurité</div>
                          <div className="text-white font-bold">À surveiller</div>
                        </div>
                        <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                          <div className="text-orange-400 text-sm font-medium mb-1">Performance</div>
                          <div className="text-white font-bold">Optimisable</div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        {cmsData?.name === 'WordPress' && (
                          <div className="flex items-start space-x-3 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg">
                            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-amber-200">Attention aux plugins non mis à jour</p>
                          </div>
                        )}
                        <div className="flex items-start space-x-3 p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
                          <Shield className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-green-200">
                            <p className="font-medium mb-1">Recommandations :</p>
                            <ul className="space-y-1 text-xs">
                              <li className="flex items-center gap-2">
                                <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                                <span>Mises à jour régulières</span>
                              </li>
                              <li className="flex items-center gap-2">
                                <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                                <span>Sauvegardes automatiques</span>
                              </li>
                              <li className="flex items-center gap-2">
                                <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                                <span>Sécurisation admin</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Analyse SEO - Fond blanc */}
      <section className="bg-white pt-[70px] pb-[140px]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Section Keywords Performance - Table Enhanced Style */}
            <div className="animate-fadeSlideUp" style={{animationDelay: '500ms'}}>
              <div className="max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-8 items-start animate-fadeSlideUp" style={{animationDelay: '500ms'}}>
                    
                    {/* Colonne gauche - Informations */}
                    <div>
                      <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Analyse SEO
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-4">
                        Performance des <span className="text-orange-600">Mots-clés</span> dans Google Suisse
                      </h3>
                      <p className="text-lg text-gray-600 mb-6">
                        Positionnement SEO et visibilité avec analyse détaillée par mot-clé
                      </p>
                      
                      {/* Encadré informatif */}
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-orange-600">5 mots-clés trouvés</span> pour{' '}
                          <span className="font-medium text-orange-700">{getDomainFromProspect(prospect)}</span>
                        </p>
                      </div>

                      {/* Points clés */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                            <BarChart3 className="w-4 h-4" />
                          </div>
                          <span className="text-gray-700 font-medium">Analyse complète du positionnement SEO</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                            <TrendingUp className="w-4 h-4" />
                          </div>
                          <span className="text-gray-700 font-medium">Opportunités d'amélioration identifiées</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                            <Target className="w-4 h-4" />
                          </div>
                          <span className="text-gray-700 font-medium">Recommandations personnalisées incluses</span>
                        </div>
                      </div>
                    </div>

                    {/* Colonne droite - Tableau des performances */}
                    <div>
                      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                        {/* En-tête du tableau */}
                        <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-4 border-b border-orange-200">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-900">Tableau des performances</h4>
                            <button className="text-xs text-orange-600 hover:text-orange-700 font-medium">
                              Tout développer →
                            </button>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">Page 1 / 1</p>
                        </div>
                        
                        {/* Tableau */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MOT-CLÉ</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VOLUME</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">POSITION</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MARQUE?</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OPPORTUNITÉ</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              <tr className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">services suisse</td>
                                <td className="px-4 py-4 text-sm text-gray-600">470</td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <ArrowDown className="w-3 h-3 mr-1" />
                                    33
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <Check className="w-3 h-3 mr-1" />
                                    Non
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="text-xs font-medium text-green-600">Forte — potentiel élevé</span>
                                </td>
                              </tr>
                              <tr className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">silver-international</td>
                                <td className="px-4 py-4 text-sm text-gray-600">249</td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <ArrowUp className="w-3 h-3 mr-1" />
                                    10
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <Check className="w-3 h-3 mr-1" />
                                    Oui
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="text-xs font-medium text-red-600">Faible — déjà acquis</span>
                                </td>
                              </tr>
                              <tr className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">silver-international avis</td>
                                <td className="px-4 py-4 text-sm text-gray-600">92</td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    <Minus className="w-3 h-3 mr-1" />
                                    30
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <Check className="w-3 h-3 mr-1" />
                                    Non
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="text-xs font-medium text-orange-600">Moyenne / faible</span>
                                </td>
                              </tr>
                              <tr className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">silver-international contact</td>
                                <td className="px-4 py-4 text-sm text-gray-600">102</td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <ArrowDown className="w-3 h-3 mr-1" />
                                    33
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <Check className="w-3 h-3 mr-1" />
                                    Oui
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="text-xs font-medium text-red-600">Faible — navigation</span>
                                </td>
                              </tr>
                              <tr className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">silver-international services</td>
                                <td className="px-4 py-4 text-sm text-gray-600">37</td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <ArrowUp className="w-3 h-3 mr-1" />
                                    10
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <Check className="w-3 h-3 mr-1" />
                                    Oui
                                  </span>
                                </td>
                                <td className="px-4 py-4">
                                  <span className="text-xs font-medium text-red-600">Faible — déjà acquis</span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                </div>

                {/* Section Recommandations style sombre */}
                <div className="mt-12 bg-gray-800 rounded-xl p-6 border border-gray-700 animate-fadeSlideUp" style={{animationDelay: '600ms'}}>
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-orange-500/20 rounded-lg flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                        <span>Recommandations :</span>
                        <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full font-medium">
                          Potentiel élevé
                        </span>
                      </h4>
                      <div className="space-y-3 text-sm text-gray-300">
                        <p className="leading-relaxed">
                          Le nombre limité de mots-clés détectés <span className="font-semibold text-orange-400">(5)</span> indique un potentiel inexploité. 
                          Une stratégie SEO ciblée pourrait <span className="font-semibold text-orange-400">multiplier votre visibilité par 3</span> et attirer un public 
                          hautement qualifié vers votre site.
                        </p>
                        <p className="leading-relaxed">
                          En travaillant 3 à 5 mots-clés non-brand, vous pourriez capter jusqu'à{' '}
                          <span className="font-semibold text-green-400 bg-green-900/30 px-2 py-1 rounded">476 visiteurs supplémentaires par mois</span>{' '}
                          avec une intention réelle d'achat.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Section Citations IA - Fond blanc */}
      <section className="bg-white pt-[70px] pb-[140px]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* AI Citations Section */}
            <div className="animate-fadeSlideUp" style={{animationDelay: '550ms'}}>
              <div className="text-center mb-12">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                  <Brain className="w-4 h-4 mr-2" />
                  Visibilité IA
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Citations générées par l'IA
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Votre présence dans les principaux moteurs de recherche et assistants IA
                </p>
              </div>

              {/* Grid des citations IA */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                
                {/* Google */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Google</h3>
                        <p className="text-xs text-gray-500">Vue d'ensemble de l'IA</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-gray-900">0</span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">0</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ChatGPT */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">ChatGPT</h3>
                        <p className="text-xs text-gray-500">OpenAI</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-gray-900">0</span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">0</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Perplexity */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Perplexity</h3>
                        <p className="text-xs text-gray-500">AI Search</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-cyan-600">2</span>
                      <span className="text-sm text-green-600 font-medium">+2</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">2 <span className="text-green-600 text-xs">+2</span></span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gemini */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Gemini</h3>
                        <p className="text-xs text-gray-500">Google AI</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-gray-900">0</span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">0</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Copilot */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Copilot</h3>
                        <p className="text-xs text-gray-500">Microsoft</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-blue-600">1</span>
                      <span className="text-sm text-green-600 font-medium">+1</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">1 <span className="text-green-600 text-xs">+1</span></span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Info box */}
              <div className="mt-8 bg-orange-50 border border-orange-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                    <Brain className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Pourquoi c'est important ?
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Les assistants IA deviennent la nouvelle porte d'entrée vers l'information. Être cité par ces plateformes 
                      augmente considérablement votre <span className="font-semibold text-orange-600">visibilité et votre crédibilité</span> auprès 
                      d'une audience qualifiée qui utilise l'IA pour ses recherches professionnelles.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Section Recommandations IA - Fond gris clair */}
      <section className="bg-gray-50 pt-[70px] pb-[140px]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* AI Recommendations - Style Minimaliste */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden animate-fadeSlideUp" style={{animationDelay: '600ms'}}>
              {/* Header minimaliste */}
              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                    <Brain className="w-4 h-4 mr-2" />
                    Recommandations IA
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Plan d'action <span className="text-orange-600">personnalisé</span>
                  </h3>
                  <p className="text-lg text-gray-600">
                    Nos recommandations basées sur l'IA pour améliorer votre performance digitale
                  </p>
                </div>

                {/* Gestion des états de chargement et d'erreur */}
                {isLoadingAi ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 text-orange-500 animate-spin mb-4" />
                    <p className="text-gray-500">Génération des recommandations en cours...</p>
                  </div>
                ) : aiError ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                    <p className="text-red-600 mb-4">{aiError}</p>
                    <button 
                      onClick={refreshAIRecommendations}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Réessayer
                    </button>
                  </div>
                ) : (
                  /* Liste des recommandations - Style minimaliste */
                  <div className="space-y-6 mb-8">
                    {(aiRecommendations.length > 0 ? aiRecommendations : mockData.recommendations).map((recommendation, index) => {
                      // Traitement des recommandations string (format IA) vs object (format mock)
                      const isStringRecommendation = typeof recommendation === 'string';
                      
                      if (isStringRecommendation) {
                        // Séparer automatiquement au premier point pour les recommandations IA
                        const firstSentenceEnd = recommendation.indexOf('.');
                        let title = recommendation;
                        let solution = '';
                        
                        if (firstSentenceEnd !== -1 && firstSentenceEnd < recommendation.length - 1) {
                          title = recommendation.substring(0, firstSentenceEnd + 1).trim();
                          solution = recommendation.substring(firstSentenceEnd + 1).trim();
                        }
                        
                        return (
                          <div key={index} className="border-l-4 border-orange-500 pl-6 py-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
                                  {index + 1}
                                </div>
                                <h4 className="font-semibold text-gray-900">
                                  {title}
                                </h4>
                              </div>
                              <span className="text-xs px-2 py-1 rounded-full font-medium bg-orange-100 text-orange-700">
                                Élevée
                              </span>
                            </div>
                            {solution && (
                              <div className="bg-gray-50 rounded-lg p-4 ml-9">
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium text-orange-600">Solution:</span> {solution}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        // Format object (données mock)
                        return (
                          <div key={index} className="border-l-4 border-orange-500 pl-6 py-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
                                  {index + 1}
                                </div>
                                <h4 className="font-semibold text-gray-900">
                                  {recommendation.title}
                                </h4>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                recommendation.priority === 'high' 
                                  ? 'bg-orange-100 text-orange-700' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {recommendation.priority === 'high' ? 'Élevée' : 'Moyenne'}
                              </span>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4 ml-9">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium text-orange-600">Solution:</span> {recommendation.solution}
                              </p>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>

              {/* Footer noir simplifié */}
              <div className="bg-gray-800 rounded-b-3xl p-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-orange-500/20 rounded-lg flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3">
                      Recommandations générales
                    </h4>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      Ces optimisations peuvent <span className="font-semibold text-orange-400">améliorer significativement votre performance</span>. 
                      Commencez par les actions prioritaires pour un impact maximal.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Quiz Completion CTA - Card blanche */}
            <div className="mt-16">
              <QuizCompletionCta prospect={prospect ? {
                token: prospect.token,
                quiz_fait: prospect.quiz_fait,
                quiz_commence: prospect.quiz_commence
              } : undefined} />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      {prospect && (
        <footer className="bg-white py-8 border-t border-gray-100">
          <div className="container mx-auto px-4 text-center max-w-2xl">
            <p className="text-xs text-gray-500">
              Cette fiche est issue d'une analyse basée uniquement sur des données publiques et des tendances sectorielles pour {prospect.site_url ? extractDomain(prospect.site_url) : 'cette entreprise'}. 
              Elle ne contient aucune information personnelle ni confidentielle, et respecte pleinement les réglementations suisses et européennes sur la protection des données.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Son seul but : offrir aux dirigeants une lecture neutre et utile de leur présence numérique. Cette page est volontairement exclue des moteurs de recherche.
            </p>
          </div>
        </footer>
      )}

      {/* Exit Popup */}
      {prospect && prospect.id && (
        <ExitPopupDemo 
          prospectId={prospect.id} 
          options={{
            sensitivity: 20,
            delay: 1000,
            onlyOnce: true
          }}
        />
      )}
    </div>
  );
};

export default TestPageRefactored;
